import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Check, CheckCheck, AlertTriangle, ChevronDown, ChevronRight, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";
import { useAppSettings } from "../lib/useAppSettings";

const CONF = {
  gc_listed: ["GC-listed", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  rule_explicit_offset: ["Rule · explicit offset", "bg-blue-50 text-blue-800 border-blue-200"],
  rule_defaulted_offset: ["Rule · defaulted offset", "bg-amber-50 text-amber-800 border-amber-200"],
  no_anchor: ["No anchor found", "bg-gray-100 text-gray-600 border-gray-200"],
};

const flagTone = (f) => (f.startsWith("needs acceleration") || f.startsWith("before-conflict") ? "text-red-700 bg-red-50 border-red-200"
  : f.startsWith("missing") || f.startsWith("anchor missing") ? "text-amber-800 bg-amber-50 border-amber-200"
    : "text-gray-600 bg-gray-50 border-gray-200");

/** DEMO-2B — proposed scope dates from the GC schedule. The PM confirms; nothing is written until Accept. */
export default function GcDateProposalPanel({ project, onAccepted }) {
  const settings = useAppSettings();
  const [proposal, setProposal] = useState(null);
  const [running, setRunning] = useState(false);
  const [deltasOnly, setDeltasOnly] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [busyKey, setBusyKey] = useState("");

  useEffect(() => {
    api.get(`/projects/${project.id}/gc-dates`).then((r) => setProposal(r.data.last_run || null)).catch(() => {});
  }, [project.id]);

  if (!settings.gc_rule_proposals) return null;

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/gc-dates/run`);
      setProposal(data); setDeltasOnly(!!data.has_accepted);
      toast.success(`${data.rows.length} scope${data.rows.length === 1 ? "" : "s"} proposed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };
  const override = async (key, patch) => {
    setBusyKey(key);
    try { const { data } = await api.patch(`/projects/${project.id}/gc-dates/overrides`, { key, ...patch }); setProposal(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Recompute failed"); }
    finally { setBusyKey(""); }
  };
  const accept = async (keys, all = false) => {
    setBusyKey(all ? "_all" : keys[0]);
    try {
      const { data } = await api.post(`/projects/${project.id}/gc-dates/accept`, { keys, all });
      setProposal(data.proposal);
      toast.success(`${data.written.length} date${data.written.length === 1 ? "" : "s"} written to the schedule`);
      onAccepted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Accept failed"); }
    finally { setBusyKey(""); }
  };

  const rows = (proposal?.rows || []).filter((r) => !deltasOnly || r.delta);
  const acceptable = (proposal?.rows || []).filter((r) => r.start && (!r.accepted || r.delta));

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="gc-date-proposal-panel">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <Wand2 className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Proposed scope dates from GC schedule</span>
        {proposal && (
          <span className="text-xs text-gray-400" data-testid="gc-proposal-meta">
            run {(proposal.run_at || "").slice(0, 16).replace("T", " ")} · GC schedule {proposal.log?.schedule_date} · {proposal.activity_count} activities
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {proposal?.has_accepted && (
            <label className="text-xs text-gray-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={deltasOnly} onChange={(e) => setDeltasOnly(e.target.checked)} data-testid="gc-proposal-deltas-only" />
              changes since last accept only
            </label>
          )}
          {proposal && acceptable.length > 0 && (
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busyKey} onClick={() => accept([], true)} data-testid="gc-proposal-accept-all">
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Accept all ({acceptable.length})
            </Button>
          )}
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" disabled={running} onClick={run} data-testid="gc-proposal-run">
            <Play className="w-3.5 h-3.5 mr-1" /> {running ? "Running…" : proposal ? "Re-run" : "Run"}
          </Button>
        </div>
      </div>

      {!proposal ? (
        <div className="px-4 py-5 text-sm text-gray-500" data-testid="gc-proposal-empty">
          Run proposes a start for every ABS scope from the GC's anchor activities (tile, final paint, …) and a finish from
          budgeted hours ÷ (crew × hours/day) in workdays. You confirm each row before anything is written.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gc-proposal-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 bg-white">
                  <th className="text-left px-3 py-1.5 font-medium">Scope</th>
                  <th className="text-left px-2 py-1.5 font-medium">Start</th>
                  <th className="text-left px-2 py-1.5 font-medium">Finish</th>
                  <th className="text-left px-2 py-1.5 font-medium">Rule · anchor</th>
                  <th className="text-left px-2 py-1.5 font-medium">Hours</th>
                  <th className="text-left px-2 py-1.5 font-medium">Crew</th>
                  <th className="text-left px-2 py-1.5 font-medium">h/day</th>
                  <th className="text-left px-2 py-1.5 font-medium">Workdays</th>
                  <th className="text-left px-2 py-1.5 font-medium">Confidence · flags</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-4 text-xs text-gray-500" data-testid="gc-proposal-no-rows">
                    {deltasOnly ? "No proposed date differs from the last accepted dates." : "No scope could be dated — see Needs manual date below."}
                  </td></tr>
                )}
                {rows.map((r) => <ProposalRow key={r.key} r={r} busy={busyKey === r.key} onOverride={(p) => override(r.key, p)} onAccept={() => accept([r.key])} />)}
              </tbody>
            </table>
          </div>

          {proposal.needs_manual_date?.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-2.5" data-testid="gc-proposal-needs-manual">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Needs manual date</div>
              <ul className="text-xs text-gray-700 space-y-0.5">
                {proposal.needs_manual_date.map((r) => (
                  <li key={r.key} data-testid={`gc-needs-manual-${r.key}`}>
                    <b>{r.scope_code}{r.phase ? ` · ${r.phase}` : ""}</b>
                    <span className="text-gray-500"> — {r.rule_label ? `${r.rule_label}: ` : ""}missing anchor {(r.missing_anchors || []).join(", ") || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {proposal.unmatched_activities?.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-2" data-testid="gc-proposal-unmatched">
              <button type="button" onClick={() => setShowUnmatched((v) => !v)} className="text-xs text-gray-600 inline-flex items-center gap-1" data-testid="gc-proposal-unmatched-toggle">
                {showUnmatched ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {proposal.unmatched_activities.length} GC activities matched no scope or anchor alias (add aliases under Settings › Field Codes › Schedule keywords)
              </button>
              {showUnmatched && (
                <ul className="mt-1 text-xs text-gray-500 grid sm:grid-cols-2 gap-x-4">
                  {proposal.unmatched_activities.map((u, i) => <li key={i}>{u.activity_name} <span className="text-gray-400">{u.start}→{u.finish}</span></li>)}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NumCell({ value, onCommit, testId, step = 1, min = 0, disabled }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <input type="number" min={min} step={step} value={v} disabled={disabled} data-testid={testId}
           onChange={(e) => setV(e.target.value)}
           onBlur={() => { const n = Number(v); if (v !== "" && n > 0 && n !== Number(value)) onCommit(n); else setV(value ?? ""); }}
           onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
           className="h-7 w-16 border border-gray-300 rounded px-1.5 text-xs bg-white tabular-nums disabled:opacity-50" />
  );
}

function ProposalRow({ r, busy, onOverride, onAccept }) {
  const [label, tone] = CONF[r.confidence] || CONF.no_anchor;
  const inp = r.inputs || {};
  const accepted = r.accepted && !r.delta;
  return (
    <tr className={`border-t border-gray-100 align-top ${r.delta ? "bg-amber-50/40" : ""}`} data-testid={`gc-row-${r.key}`}>
      <td className="px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap">
        {r.scope_code}{r.phase && <span className="text-gray-500 font-normal"> · {r.phase}</span>}
        {r.accepted && <span className="block text-[10px] text-emerald-700" data-testid={`gc-row-accepted-${r.key}`}>accepted {(r.accepted.accepted_at || "").slice(0, 10)}</span>}
      </td>
      <td className="px-2 py-1.5 text-xs tabular-nums whitespace-nowrap" data-testid={`gc-row-start-${r.key}`}>
        {r.start || "—"}
        {r.delta && <span className="block text-[10px] text-amber-700">was {r.delta.from_start || "—"}</span>}
      </td>
      <td className="px-2 py-1.5 text-xs tabular-nums whitespace-nowrap" data-testid={`gc-row-finish-${r.key}`}>
        {r.finish || <span title={(r.flags || []).find((f) => f.startsWith("missing")) || ""}>—</span>}
        {r.delta && <span className="block text-[10px] text-amber-700">was {r.delta.from_finish || "—"} · {r.delta.cause}</span>}
      </td>
      <td className="px-2 py-1.5 text-xs text-gray-700 max-w-[260px]">
        <div className="font-medium">{r.rule_label}</div>
        {r.anchor && (
          <div className="text-gray-500" data-testid={`gc-row-anchor-${r.key}`}>
            {r.anchor.label} <span className="tabular-nums">{r.anchor.start}→{r.anchor.finish}</span>
          </div>
        )}
        {r.basis && <div className="text-[11px] text-gray-400">{r.basis}</div>}
        {(r.before_anchors || []).map((b, i) => <div key={i} className="text-[11px] text-gray-400">before {b.anchor} {b.edge} {b.date}</div>)}
      </td>
      <td className="px-2 py-1.5">
        <NumCell value={inp.scope_hours} step={0.5} disabled={busy || r.confidence === "gc_listed"} testId={`gc-row-hours-${r.key}`} onCommit={(n) => onOverride({ scope_hours: n })} />
        <div className="text-[10px] text-gray-400">{inp.hours_source}</div>
      </td>
      <td className="px-2 py-1.5">
        <NumCell value={inp.crew_size} disabled={busy || r.confidence === "gc_listed"} testId={`gc-row-crew-${r.key}`} onCommit={(n) => onOverride({ crew_size: n })} />
        <div className="text-[10px] text-gray-400">{inp.crew_source}</div>
      </td>
      <td className="px-2 py-1.5">
        <NumCell value={inp.hours_per_day} disabled={busy || r.confidence === "gc_listed"} testId={`gc-row-hpd-${r.key}`} onCommit={(n) => onOverride({ hours_per_day: n })} />
      </td>
      <td className="px-2 py-1.5 text-xs tabular-nums" data-testid={`gc-row-workdays-${r.key}`}>
        {inp.workdays ?? "—"}
        {r.confidence !== "gc_listed" && (
          <label className="block text-[10px] text-gray-500 whitespace-nowrap">
            <input type="checkbox" className="mr-1 align-middle" checked={!!r.parallel} disabled={busy} data-testid={`gc-row-parallel-${r.key}`}
                   onChange={(e) => onOverride({ parallel: e.target.checked })} />parallel
          </label>
        )}
      </td>
      <td className="px-2 py-1.5">
        <span className={`inline-block text-[10px] font-semibold rounded-full border px-2 py-0.5 ${tone}`} data-testid={`gc-row-confidence-${r.key}`}>{label}</span>
        <div className="mt-1 flex flex-wrap gap-1">
          {(r.flags || []).map((f, i) => (
            <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] rounded border px-1.5 py-0.5 ${flagTone(f)}`} data-testid={`gc-row-flag-${r.key}-${i}`}>
              {(f.startsWith("needs acceleration") || f.startsWith("before-conflict")) && <AlertTriangle className="w-3 h-3" />}{f}
            </span>
          ))}
        </div>
      </td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {r.start && !accepted && (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onAccept} data-testid={`gc-row-accept-${r.key}`}>
            <Check className="w-3.5 h-3.5 mr-1" /> Accept
          </Button>
        )}
      </td>
    </tr>
  );
}
